import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../../services/grafikService';
import {
  getAllStatuses, parseShiftTime, toISODate
} from '../../../utils/grafikHelpers';

/**
 * Bulk dodaj zmiany - dla wybranych pracownikow, zakresu dat, dni tygodnia
 * i godzin. Generuje liste { user_login, location_id, data, godzina_od, ... }
 * i wysyla via grafikService.bulkCreate(...).
 *
 * Props:
 *   locations: [{id, nazwa}]
 *   defaultLocationId?: number|string
 *   onClose()
 *   onAdded()
 */
const DAYS = [
  { value: 1, label: 'Pn' }, { value: 2, label: 'Wt' }, { value: 3, label: 'Sr' },
  { value: 4, label: 'Cz' }, { value: 5, label: 'Pt' }, { value: 6, label: 'So' },
  { value: 7, label: 'Nd' }
];

const BulkAssignModal = ({ locations = [], defaultLocationId = '', onClose, onAdded }) => {
  const today = new Date();
  const isoToday = toISODate(today);

  const [locationId, setLocationId] = useState(defaultLocationId || '');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserLogins, setSelectedUserLogins] = useState(new Set());

  const [dataOd, setDataOd] = useState(isoToday);
  const [dataDo, setDataDo] = useState(isoToday);
  const [selectedDays, setSelectedDays] = useState(new Set([1, 2, 3, 4, 5])); // Pn-Pt
  const [godzinaOd, setGodzinaOd] = useState('08:00');
  const [godzinaDo, setGodzinaDo] = useState('16:00');
  const [przerwaMinut, setPrzerwaMinut] = useState(0);
  const [status, setStatus] = useState('planowana');
  const [notatki, setNotatki] = useState('');
  const [overrideExisting, setOverrideExisting] = useState(false);
  const [busy, setBusy] = useState(false);

  // Pobierz pracownikow lokalizacji
  useEffect(() => {
    if (!locationId) {
      setUsers([]);
      setSelectedUserLogins(new Set());
      return;
    }
    let cancelled = false;
    setLoadingUsers(true);
    (async () => {
      try {
        const res = await grafikService.getUsersList({ locationId });
        const arr = res?.data?.users || res?.data || res?.users || [];
        if (cancelled) return;
        setUsers(Array.isArray(arr) ? arr : []);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Blad pobierania pracownikow');
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId]);

  // Przelicz podglad: ile zmian zostanie wygenerowane
  const previewCount = useMemo(() => {
    if (!locationId || !dataOd || !dataDo || selectedUserLogins.size === 0
        || selectedDays.size === 0 || !godzinaOd || !godzinaDo) {
      return 0;
    }
    const start = new Date(dataOd + 'T00:00:00');
    const end = new Date(dataDo + 'T00:00:00');
    if (isNaN(start) || isNaN(end) || start > end) return 0;
    let dayCount = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const jsDay = cur.getDay(); // 0=Nd..6=Sob
      const dt = jsDay === 0 ? 7 : jsDay;
      if (selectedDays.has(dt)) dayCount++;
      cur.setDate(cur.getDate() + 1);
    }
    return dayCount * selectedUserLogins.size;
  }, [locationId, dataOd, dataDo, selectedUserLogins, selectedDays, godzinaOd, godzinaDo]);

  const toggleUser = (login) => {
    setSelectedUserLogins(prev => {
      const next = new Set(prev);
      if (next.has(login)) next.delete(login); else next.add(login);
      return next;
    });
  };

  const selectAllUsers = () => setSelectedUserLogins(new Set(users.map(u => u.login)));
  const deselectAllUsers = () => setSelectedUserLogins(new Set());

  const toggleDay = (dt) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(dt)) next.delete(dt); else next.add(dt);
      return next;
    });
  };

  const loadHoursFromLocation = async () => {
    if (!locationId) {
      toast.error('Najpierw wybierz lokalizacje');
      return;
    }
    try {
      const res = await grafikService.getLocationHours(locationId);
      const list = Array.isArray(res?.data) ? res.data
                 : Array.isArray(res?.data?.data) ? res.data.data
                 : Array.isArray(res) ? res
                 : [];
      // Wez pierwszy nie-zamkniety dzien z ustawionymi godzinami
      const entry = list.find(h => !h.zamkniete && h.godzina_od && h.godzina_do);
      if (!entry) {
        toast.error('Brak skonfigurowanych godzin otwarcia');
        return;
      }
      const od = parseShiftTime(entry.godzina_od);
      const dop = parseShiftTime(entry.godzina_do);
      if (od) setGodzinaOd(od);
      if (dop) setGodzinaDo(dop);
      toast.success(`Zaladowano: ${od}-${dop}`);
    } catch (err) {
      toast.error(err.message || 'Blad pobrania godzin lokalizacji');
    }
  };

  const handleSubmit = async () => {
    if (!locationId) { toast.error('Wybierz lokalizacje'); return; }
    if (selectedUserLogins.size === 0) { toast.error('Wybierz co najmniej jednego pracownika'); return; }
    if (selectedDays.size === 0) { toast.error('Wybierz co najmniej jeden dzien tygodnia'); return; }
    if (!dataOd || !dataDo) { toast.error('Wybierz zakres dat'); return; }
    const start = new Date(dataOd + 'T00:00:00');
    const end = new Date(dataDo + 'T00:00:00');
    if (start > end) { toast.error('Data Od musi byc <= Data Do'); return; }
    if (!godzinaOd || !godzinaDo) { toast.error('Podaj godziny od/do'); return; }

    if (overrideExisting) {
      toast.error('Tryb "Zastap" nie jest jeszcze obslugiwany przez backend - uzyj "Pomin"');
      return;
    }

    // Generuj liste shifts
    const shifts = [];
    const cur = new Date(start);
    while (cur <= end) {
      const jsDay = cur.getDay();
      const dt = jsDay === 0 ? 7 : jsDay;
      if (selectedDays.has(dt)) {
        const dataIso = toISODate(cur);
        for (const login of selectedUserLogins) {
          shifts.push({
            user_login: login,
            location_id: Number(locationId) || locationId,
            data: dataIso,
            godzina_od: godzinaOd,
            godzina_do: godzinaDo,
            przerwa_minut: Number(przerwaMinut) || 0,
            status,
            notatki: notatki || null
          });
        }
      }
      cur.setDate(cur.getDate() + 1);
    }

    if (shifts.length === 0) {
      toast.error('Brak zmian do dodania - sprawdz zakres dat / dni tygodnia');
      return;
    }

    setBusy(true);
    try {
      const res = await grafikService.bulkCreate(shifts);
      const data = res?.data || res || {};
      const created = data.created ?? data.inserted ?? shifts.length;
      const errors = Array.isArray(data.errors) ? data.errors : [];
      const skipped = errors.length;
      const msg = skipped > 0
        ? `Dodano ${created} zmian (${skipped} pominieto - konflikt/blad)`
        : `Dodano ${created} zmian`;
      toast.success(msg);
      if (onAdded) onAdded();
      if (onClose) onClose();
    } catch (err) {
      toast.error(err.message || 'Blad bulk dodawania zmian');
    } finally {
      setBusy(false);
    }
  };

  // Style
  const inputStyle = {
    width: '100%', padding: '0.45rem',
    border: '1px solid var(--border)', borderRadius: '4px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '0.85rem'
  };
  const labelStyle = {
    display: 'block', marginBottom: '0.25rem',
    fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1001, padding: '1rem'
    }} onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px',
        width: '100%', maxWidth: '720px',
        maxHeight: '90vh', overflowY: 'auto',
        border: '1px solid var(--border)', boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)',
          backgroundColor: 'var(--bg)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
            <i className="fas fa-users-cog" style={{ marginRight: '0.5rem' }} />
            Bulk dodaj zmiany
          </h4>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer' }}>
            &#x2715;
          </button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {/* Lokalizacja */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Lokalizacja *</label>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              style={inputStyle}
            >
              <option value="">— wybierz —</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.nazwa || l.name}</option>
              ))}
            </select>
          </div>

          {/* Pracownicy */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '0.3rem'
            }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>
                Pracownicy * ({selectedUserLogins.size}/{users.length})
              </label>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button type="button" onClick={selectAllUsers}
                  disabled={!users.length}
                  style={{
                    padding: '4px 10px', fontSize: '11px',
                    backgroundColor: 'transparent', color: 'var(--info)',
                    border: '1px solid var(--info)', cursor: 'pointer'
                  }}>Zaznacz wszystkich</button>
                <button type="button" onClick={deselectAllUsers}
                  disabled={!users.length}
                  style={{
                    padding: '4px 10px', fontSize: '11px',
                    backgroundColor: 'transparent', color: 'var(--text-secondary)',
                    border: '1px solid var(--border)', cursor: 'pointer'
                  }}>Odznacz wszystkich</button>
              </div>
            </div>
            <div style={{
              maxHeight: '160px', overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: '4px',
              padding: '0.4rem 0.6rem', backgroundColor: 'var(--bg)'
            }}>
              {loadingUsers && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Ladowanie pracownikow...
                </div>
              )}
              {!loadingUsers && !locationId && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Wybierz lokalizacje aby zaladowac pracownikow.
                </div>
              )}
              {!loadingUsers && locationId && users.length === 0 && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Brak aktywnych pracownikow w tej lokalizacji.
                </div>
              )}
              {!loadingUsers && users.map(u => (
                <label key={u.login} style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  fontSize: '0.85rem', padding: '0.15rem 0', cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedUserLogins.has(u.login)}
                    onChange={() => toggleUser(u.login)}
                  />
                  <span>{u.login}{u.typ ? ` (${u.typ})` : ''}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Okres */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Data od *</label>
              <input type="date" value={dataOd} onChange={(e) => setDataOd(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Data do *</label>
              <input type="date" value={dataDo} onChange={(e) => setDataDo(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Dni tygodnia */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Dni tygodnia *</label>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {DAYS.map(d => (
                <label key={d.value} style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.3rem 0.55rem',
                  border: `1px solid ${selectedDays.has(d.value) ? 'var(--info)' : 'var(--border)'}`,
                  backgroundColor: selectedDays.has(d.value) ? 'var(--info-light)' : 'var(--card-bg)',
                  borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer',
                  fontWeight: selectedDays.has(d.value) ? 600 : 400,
                  color: selectedDays.has(d.value) ? 'var(--info)' : 'var(--text-secondary)'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedDays.has(d.value)}
                    onChange={() => toggleDay(d.value)}
                    style={{ margin: 0 }}
                  />
                  {d.label}
                </label>
              ))}
            </div>
          </div>

          {/* Godziny */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div>
              <label style={labelStyle}>Godzina od *</label>
              <input type="time" value={godzinaOd} onChange={(e) => setGodzinaOd(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Godzina do *</label>
              <input type="time" value={godzinaDo} onChange={(e) => setGodzinaDo(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Przerwa (min)</label>
              <input type="number" min="0" step="5"
                value={przerwaMinut}
                onChange={(e) => setPrzerwaMinut(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={loadHoursFromLocation}
              disabled={!locationId}
              style={{
                padding: '4px 10px', fontSize: '11px',
                backgroundColor: 'transparent', color: 'var(--info)',
                border: '1px dashed var(--info)',
                cursor: locationId ? 'pointer' : 'not-allowed'
              }}
            >
              <i className="fas fa-lightbulb" style={{ marginRight: '0.3rem' }} />
              Zaladuj z godzin otwarcia lokalizacji
            </button>
          </div>

          {/* Status + Notatki */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
                {getAllStatuses().map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Notatki</label>
              <input type="text" value={notatki} onChange={(e) => setNotatki(e.target.value)}
                placeholder="opcjonalne" style={inputStyle} />
            </div>
          </div>

          {/* Tryb istniejacych */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Co zrobic z istniejacymi zmianami w tym okresie?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                <input
                  type="radio" name="bulk-mode"
                  checked={!overrideExisting}
                  onChange={() => setOverrideExisting(false)}
                />
                Pomin (nie nadpisuj istniejacych)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem',
                opacity: 0.55, cursor: 'not-allowed' }} title="Nieobslugiwane przez backend (TODO)">
                <input
                  type="radio" name="bulk-mode"
                  checked={overrideExisting}
                  onChange={() => setOverrideExisting(true)}
                  disabled
                />
                Zastap (kasuj istniejace i dodaj nowe) - <em>do dokonczenia</em>
              </label>
            </div>
          </div>

          {/* Podglad */}
          <div style={{
            padding: '0.6rem 0.8rem',
            backgroundColor: 'var(--bg)',
            borderLeft: '3px solid var(--info)',
            borderRadius: '4px',
            fontSize: '0.85rem', color: 'var(--text)',
            marginBottom: '1rem'
          }}>
            <i className="fas fa-eye" style={{ marginRight: '0.4rem', color: 'var(--info)' }} />
            <strong>Podglad:</strong>{' '}
            {selectedUserLogins.size} pracownikow x dni pasujace = <strong>{previewCount}</strong> zmian do dodania
          </div>

          {/* Przyciski */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
            paddingTop: '0.75rem', borderTop: '1px solid var(--border)'
          }}>
            <button type="button" onClick={onClose}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px'
              }}
            >Anuluj</button>
            <button type="button" onClick={handleSubmit} disabled={busy || previewCount === 0}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--info)', color: '#fff',
                border: 'none', cursor: (busy || previewCount === 0) ? 'not-allowed' : 'pointer',
                fontSize: '12px', fontWeight: 600,
                opacity: (busy || previewCount === 0) ? 0.55 : 1
              }}
            >
              {busy ? 'Dodaje...' : `Dodaj ${previewCount} zmian`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;
